import React, { useState } from 'react';
import { Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { constructCommentPayload } from '../utils/PayloadUtils';
import { getCommentPostUrl } from '../utils/UrlUtilities';
import { useApiCaller } from '../utils/ApiUtilities';

const PURPLE_GRADIENT = 'linear-gradient(145deg,#7c3aed,#5b21b6)';
const headingStyle = { color: '#5b21b6', fontWeight: 700 };

const ContactPage = () => {
    const { userState } = useUser();
    const [formData, setFormData] = useState({
        name: userState.name,
        email: userState.email,
        message: ''
    });
    const [status, setStatus] = useState(''); // 'success', 'error', or ''
    const [errorMsg, setErrorMsg] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const CallApi = useApiCaller();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus(''); // Clear previous status
        setIsSubmitting(true);

        // Basic client-side validation
        if (!formData.name || !formData.email || !formData.message) {
            setStatus('error');
            setIsSubmitting(false);
            return;
        }

        // Simulate API call
        const newCommentPayload = constructCommentPayload(formData.message, 'pending');
        console.log("post payload : ", newCommentPayload);
        const postCommentResponse = await CallApi({
            url: getCommentPostUrl(),
            method: 'POST',
            payload: newCommentPayload,
            token: true,
        });
        console.log(`Response from api `, postCommentResponse);

        if (postCommentResponse.success) {
            setStatus('success');
            setFormData({
                name: userState.name,
                email: userState.email,
                message: ''
            }); // Clear form
        } else {
            setStatus('error');
            setErrorMsg(postCommentResponse?.data?.errorMessage);
        }
        setIsSubmitting(false);
    };

    const isFormValid = !formData.name || !formData.email || !formData.message;

    return (
        <div className="container-fluid py-5" style={{ background: '#f5f3ff', minHeight: '100vh' }}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-9">
                        <div className="card shadow-sm" style={{ border: '1px solid #ede9fe', borderRadius: '0.9rem', overflow: 'hidden' }}>
                            <div style={{ background: PURPLE_GRADIENT, color: '#fff', padding: '2rem 1.5rem', textAlign: 'center' }}>
                                <h1 className="fs-2 fw-bold mb-2">Contact Us</h1>
                                <p className="mb-0" style={{ opacity: 0.92, maxWidth: 640, margin: '0 auto' }}>
                                    Have a question, suggestion, or feedback? We'd love to hear from you — send us a message or reach out using the details below.
                                </p>
                            </div>
                            <div className="card-body p-4 p-md-5">
                            <div className="row g-4">
                                <div className="col-md-6">
                                    <h2 className="fs-4 mb-3" style={headingStyle}>
                                        Send Us a Message
                                    </h2>
                                    <form onSubmit={handleSubmit}>
                                        <div className="mb-3">
                                            <label htmlFor="name" className="form-label fw-semibold" style={{ color: '#5b21b6' }}>Your Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                aria-describedby="nameHelp"
                                                readOnly
                                            />
                                            {status === 'error' && !formData.name && <div className="text-danger mt-1">Name is required.</div>}
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="email" className="form-label fw-semibold" style={{ color: '#5b21b6' }}>Your Email</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                aria-describedby="emailHelp"
                                                readOnly
                                            />
                                            {status === 'error' && !formData.email && <div className="text-danger mt-1">Email is required.</div>}
                                        </div>
                                        <div className="mb-4">
                                            <label htmlFor="message" className="form-label fw-semibold" style={{ color: '#5b21b6' }}>Your Message <span className="text-danger">*</span></label>
                                            <textarea
                                                className="form-control"
                                                id="message"
                                                name="message"
                                                rows="6"
                                                value={formData.message}
                                                onChange={handleChange}
                                                required
                                                aria-describedby="messageHelp"
                                            ></textarea>
                                            {status === 'error' && !formData.message && <div className="text-danger mt-1">Message is required.</div>}
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn btn-lg w-100 rounded-pill text-white fw-semibold"
                                            style={{ background: PURPLE_GRADIENT, border: 'none', boxShadow: '0 3px 0 #4c1d95, 0 4px 10px rgba(124,58,237,0.4)' }}
                                            disabled={isFormValid}
                                        >
                                            {isSubmitting ? 'Sending...' : 'Submit Message'}
                                        </button>

                                        {status === 'success' && (
                                            <div className="alert alert-success mt-3 text-center" role="alert">
                                                Thank you for your message! We'll get back to you soon.
                                            </div>
                                        )}
                                        {status === 'error' && (
                                            <div className="alert alert-danger mt-3 text-center" role="alert">
                                                {errorMsg || 'Failed to send message. Please try again or fill all fields.'}
                                            </div>
                                        )}
                                    </form>
                                </div>
                                <div className="col-md-6">
                                    <h2 className="fs-4 mb-3" style={headingStyle}>
                                        Our Contact Details
                                    </h2>
                                    <div className="mb-3">
                                        <h5 className="fw-semibold" style={headingStyle}>Email:</h5>
                                        <p className="text-secondary">
                                            <a href="mailto:support@kobraprep.com" className="text-decoration-none" style={{ color: '#7c3aed' }}>
                                                support@kobraprep.com
                                            </a>
                                        </p>
                                    </div>
                                    <div className="mb-3">
                                        <h5 className="fw-semibold" style={headingStyle}>Phone:</h5>
                                        <p className="text-secondary">
                                            <a href="tel:+19514105432" className="text-decoration-none" style={{ color: '#7c3aed' }}>
                                                +1 (951) 410-5432
                                            </a>
                                        </p>
                                    </div>
                                    <div className="mb-3">
                                        <h5 className="fw-semibold" style={headingStyle}>Address:</h5>
                                        <p className="text-secondary">
                                            480 Oak Grove Dr<br/>
                                            Santa Clara, CA 95054<br/>
                                            USA
                                        </p>
                                    </div>
                                    <div className="mt-4">
                                        <h5 className="fw-semibold" style={headingStyle}>Follow Us:</h5>
                                        <div className="d-flex gap-3 mt-2">
                                            <a href="#" aria-label="Facebook" style={{ color: '#1877F2' }}><Facebook size={22} /></a>
                                            <a href="#" aria-label="Twitter" style={{ color: '#1DA1F2' }}><Twitter size={22} /></a>
                                            <a href="#" aria-label="Instagram" style={{ color: '#E4405F' }}><Instagram size={22} /></a>
                                            <a href="#" aria-label="LinkedIn" style={{ color: '#0A66C2' }}><Linkedin size={22} /></a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactPage;
